import dayjs from 'dayjs'
import { PaymentIntent } from '@stripe/stripe-js'

import { Classified, ClassifiedAlert } from '../rest/types/Classified'
import { AddFeedbackParams } from '../rest/requests/feedbacks/addFeedback'
import { Offer } from '../rest/types/Offer'
import { ToggleReservationResponse } from '../rest/requests/reservations/toggleReservation'
import { AdsReplyFormValues } from '../forms/AdsReplyForm'
import { Provider, User } from '../rest/types/User'
import { isInPast } from '../helpers/DateHelpers'
import { Statistics } from '../rest/types/Statistics'
import Router from '../routes/Router'

const trackEvent = (event: any) => {
  if (
    typeof window === 'undefined' ||
    (window as any).dataLayer === undefined
  ) {
    return null
  }

  return (window as any).dataLayer.push(event)
}

const subscriptionType = (user?: User | null) => {
  if (!user?.username) return 'not_connected'
  return user?.provider === Provider.Local
    ? 'email'
    : user?.provider || 'not_connected'
}

export default {
  pageview: ({
    user,
    statistics,
    path,
  }: {
    user?: User | null
    statistics?: Statistics
    path?: string | null
  }) => {
    const pageType = path ? Router.match(path)?.route?.name || 'unknown' : null
    return trackEvent({
      event: 'page_view',
      user_data: {
        login_status: user ? 'connected' : 'not_connected',
        subscription_type: subscriptionType(user),
        centrale_status: {
          member_type:
            user?.role?.name === 'Full Acess' ||
            (user && !isInPast(user.subscriptionExpiration))
              ? 'admitted'
              : 'not_admitted',
          subscription_type:
            user && isInPast(user.subscriptionExpiration)
              ? 'offered'
              : user && !isInPast(user.subscriptionExpiration)
              ? 'paid'
              : '',
          is_active: !!(
            user?.role?.name === 'Full Acess' ||
            (user && !isInPast(user.subscriptionExpiration))
          ),
        },
        user_id: user?.id,
      },
      ...(statistics && {
        content_data: statistics,
      }),
      page_data: {
        type: pageType,
        department: user?.postcode,
      },
    })
  },
  alert_creation: (data: ClassifiedAlert) => {
    return trackEvent({
      event: 'alert_creation',
      user_data: {
        user_id: data?.user?.id,
      },
      alert_data: {
        department: data?.localisation,
        filters: {
          specialties: data?.specialities?.map(
            (speciality) => speciality?.name
          ),
          categories: data?.categories?.map((category) => category.name),
          term: data?.terms,
        },
        creation_date: data?.createdAt,
      },
    })
  },
  review_submit: (data: AddFeedbackParams) => {
    return trackEvent({
      event: 'review_submit',
      offer_data: {
        id: data?.offer,
      },
      user_data: {
        user_id: data?.userId,
      },
      review_data: {
        rating: data?.rating,
        offer_url: data?.offerSlug,
        review_text: data?.text,
      },
    })
  },
  ad_creation: (data: Classified) => {
    return trackEvent({
      event: 'ad_creation',
      user_data: {
        user_id: data?.user?.id,
      },
      review_data: {
        ad_data: {
          id: data?.id,
          type: data?.classifiedCategory?.name,
          department: data?.department,
          publication_date: data?.publishedAt,
        },
      },
    })
  },
  ad_view: (data: Classified) => {
    return trackEvent({
      event: 'ad_view',
      ad_data: {
        id: data?.id,
        type: data?.classifiedCategory?.name,
        department: data?.department,
        days_active: dayjs().diff(data?.publishedAt, 'day'),
        status: data?.status,
      },
    })
  },
  offer_view: (data: Offer, user: User | null) => {
    return trackEvent({
      event: 'offer_view',
      offer_data: {
        id: data?.id,
        type: data?.categories
          ?.filter((category: any) => category?.parentCategory)
          ?.map((category: any) => category?.name),

        category: data?.categories
          ?.filter((category: any) => !category?.parentCategory)
          ?.map((category: any) => category?.name),
        days_active: dayjs().diff(data?.publishedAt, 'day'),
        status: 'active',
      },
      user_data: {
        ...(user
          ? {
              member_type:
                user?.role?.name === 'Full Acess' ||
                (user && !isInPast(user.subscriptionExpiration))
                  ? 'admitted'
                  : 'not_admitted',
              subscription_type:
                user && isInPast(user.subscriptionExpiration)
                  ? 'offered'
                  : 'paid',
            }
          : { member_type: '', subscription_type: '' }),
      },
    })
  },
  partner_click: (brandName: string, brandLinkHref: string, userId: string) => {
    return trackEvent({
      event: 'partner_click',
      user_data: {
        user_id: userId,
      },
      click_data: {
        partner_name: brandName,
        destination_url: brandLinkHref,
      },
    })
  },
  pre_reservation: (data: ToggleReservationResponse) => {
    return trackEvent({
      event: 'pre_reservation',
      user_data: {
        user_id: data?.reservation?.user,
      },
      offer_data: {
        id: data?.reservation?.offer?.id,
      },
      reservation_data: {
        offer_type: 'formation',
        timestamp: dayjs().format(),
      },
    })
  },
  ad_archive: (data: Classified) => {
    return trackEvent({
      event: 'ad_archive',
      ad_data: {
        id: data?.id,
        archive_reason: data?.filled ? 'pourvue' : 'non pourvue',
        via_urps: data?.service === 'urps',
        publication_date: data?.publishedAt,
        archive_date: data?.archivedAt,
        days_active: dayjs().diff(data?.publishedAt, 'day'),
        type: data?.classifiedCategory?.name,
        department: data?.department,
      },
    })
  },
  ad_hide: (data: Classified) => {
    return trackEvent({
      event: 'ad_hide',
      ad_data: {
        id: data?.id,
        via_urps: data?.service === 'urps',
        publication_date: data?.publishedAt,
        hide_date: data?.hiddenAt,
        days_active: dayjs().diff(data?.publishedAt, 'day'),
        type: data?.classifiedCategory?.name,
        department: data?.department,
      },
    })
  },
  ad_remove: (data: Classified) => {
    return trackEvent({
      event: 'ad_remove',
      ad_data: {
        id: data?.id,
        via_urps: data?.service === 'urps',
        publication_date: data?.publishedAt,
        remove_date: data?.removedAt,
      },
    })
  },
  conversation_start: (data: AdsReplyFormValues) => {
    return trackEvent({
      event: 'conversation_start',
      ad_data: {
        ad_id: data?.classified,
      },
      user_data: {
        user_id: data?.answer_user,
      },
      conversation_data: {
        conversation_type: 'new',
        ad_type: data?.fullClassified?.classifiedCategory?.name,
      },
    })
  },
  message_send: (
    conversationId: string,
    adId: string,
    userId: string | undefined
  ) => {
    return trackEvent({
      event: 'message_send',
      user_data: {
        user_id: userId ?? '',
      },
      message_data: {
        conversation_id: conversationId,
        message_type: 'reply',
        ad_id: adId,
      },
    })
  },
  inscription: (data: User, method: string) => {
    return trackEvent({
      event: 'inscription',
      user_data: {
        method: method,
        user_id: data?.id,
        subscription_type: method,
        registration_date: data?.createdAt,
        department: data?.postcode,
      },
    })
  },
  connexion: (user: User | undefined) => {
    return trackEvent({
      event: 'connexion',
      user_data: {
        method: user?.provider ?? '',
        user_id: user?.id ?? '',
        subscription_type: user?.provider ?? '',
        centrale_member_type:
          user?.role?.name === 'Full Acess' ||
          (user && !isInPast(user.subscriptionExpiration))
            ? 'admitted'
            : 'not_admitted',
      },
    })
  },
  new_subscription: (
    userId: string | undefined,
    paymentIntent: PaymentIntent
  ) => {
    return trackEvent({
      event: 'new_subscription',
      user_data: {
        user_id: userId ?? '',
      },
      subscription_data: {
        type: 'centrale',
        payment_status: paymentIntent.status,
        is_first_time: true,
        amount: paymentIntent.amount / 100,
        member_type: 'paid',
      },
    })
  },
  subscription_renewal: (
    userId: string | undefined,
    paymentIntent: PaymentIntent
  ) => {
    return trackEvent({
      event: 'subscription_renewal',
      user_data: {
        user_id: userId,
      },
      subscription_data: {
        type: 'centrale',
        payment_status: paymentIntent.status,
        is_first_time: false,
        amount: paymentIntent.amount / 100,
        member_type: 'paid',
      },
    })
  },
  search_pa: (
    terms: string,
    localization: string,
    categories: (string | undefined)[],
    specialities: string[],
    numberClassifieds: number,
    userId?: string,
    sessionId?: string
  ) => {
    return trackEvent({
      event: 'search_pa',
      user_data: {
        user_id: userId ?? '',
      },
      search_data: {
        keyword: terms,
        filters: {
          department: localization,
          categories: categories,
          specialities: specialities,
        },
        results_count: numberClassifieds,
        session_id: sessionId ?? '',
      },
    })
  },
  search_centrale: (
    search: string | undefined,
    total: number,
    filteredCategories: any,
    userId?: string,
    sessionId?: string
  ) => {
    return trackEvent({
      event: 'search_centrale',
      user_data: {
        user_id: userId ?? '',
      },
      search_data: {
        keyword: search ?? '',
        filters: { category: filteredCategories },
        results_count: total ?? 0,
        session_id: sessionId ?? '',
      },
    })
  },
}
